/* This component can be used on its own, but also in combination with PopoverOverlay -> go
 there for more info */

import { useTranslation } from 'react-i18next'
import { cloneElement, ReactElement, useRef, useState } from 'react'
import OverlaySelectWrapper from '@/components/common/propertyPopoverOverlay/overlay/OverlaySelectWrapper.tsx'
import OverlaySelectOpened from '@/components/common/propertyPopoverOverlay/overlay/OverlaySelectOpened.tsx'
import useHandleClickOutside from '@/lib/hooks/useHandleClickOutside.ts'
import PropertyPopoverTrigger from '@/components/common/propertyPopoverOverlay/PropertyPopoverTrigger.tsx'
import { PopoverOverlayT } from '@/components/common/propertyPopoverOverlay/PopoverOverlay.tsx'
import TriggerWithCloseBtnWrapper from '@/components/common/propertyPopoverOverlay/TriggerWithCloseBtnWrapper.tsx'
import OverlayBackdrop from '@/components/common/propertyPopoverOverlay/overlay/OverlayBackdrop.tsx'
import { useOverlayStore } from '@/components/common/propertyPopoverOverlay/overlay/useOverlayStore.ts'
import { cn } from '@/lib/utils.ts'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button.tsx'

export const Overlay = ({
  onClose,
  searchLabel,
  properties,
  customTrigger,
  className,
  XBtn,
  title,
  ...props
}: PopoverOverlayT) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const disableClosingOverlay = useOverlayStore(state => state.disableClosingOverlay)

  function handleClose() {
    document.body.style.overflow = 'unset'
    onClose && onClose()
    setOpen(false)
  }

  function handleOpen() {
    setOpen(true)
    document.body.style.overflow = 'hidden'
  }

  const clickOutsideDisabled = !open || disableClosingOverlay
  useHandleClickOutside(clickOutsideDisabled, handleClose, ref)

  // clone custom trigger and attach the handleClick prop
  let customBtn = customTrigger
    ? cloneElement(customTrigger as ReactElement, { handleClick: handleOpen })
    : null

  const trigger = customBtn ?? (
    <PropertyPopoverTrigger handleClick={handleOpen} variant={XBtn ? 'ghost' : 'outline'} />
  )

  return (
    <>
      {XBtn ? (
        <TriggerWithCloseBtnWrapper
          onClick={XBtn.onClick}
          variant={XBtn.variant}
          size={XBtn.size}
          trigger={trigger}
        />
      ) : (
        trigger
      )}

      <OverlayBackdrop open={open} className={`md:hidden`} />
      <OverlaySelectWrapper open={open} handleSwipeDown={handleClose} className={`md:w-full`}>
        <div
          ref={ref}
          // Tall focused sheet (Linear-style): fixed height so the inner flex-1
          // list actually fills it (a `max-h` auto-height container collapses a
          // flex-1 child to 0). When the soft keyboard is open, the wrapper moves
          // above it and this cap shrinks the sheet to the remaining viewport.
          className={cn(
            `z-[10010] flex w-full flex-col rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)] shadow-2xl`,
            className,
          )}
          style={{
            height:
              'min(85svh, calc(100svh - var(--kbd-inset, 0px) - var(--safe-area-inset-top, 0px) - 2.5rem))',
          }}
        >
          {/* Header: optional title (Linear-style) + an ✕ close affordance. */}
          <div className={`flex shrink-0 items-center justify-between gap-2 px-4 pt-1`}>
            {title ? (
              <h2 className={`truncate text-base font-semibold text-foreground`}>{title}</h2>
            ) : (
              <span />
            )}
            <Button
              type={`button`}
              variant={`outline`}
              size={`icon`}
              onClick={handleClose}
              aria-label={t('common.cancel')}
              className={`shrink-0 text-muted-foreground`}
            >
              <X className={`size-5`} />
            </Button>
          </div>
          <OverlaySelectOpened
            properties={properties}
            // No `common.show` fallback: that is a button verb, not a search
            // prompt. Undefined lets the sheet fall back to the search wording.
            searchLabel={searchLabel}
            handleClose={handleClose}
            open={open}
            {...props}
          />
        </div>
      </OverlaySelectWrapper>
    </>
  )
}
